// client/src/components/scheduling/WeeklyCalendar.js
import { useParams, useNavigate } from 'react-router-dom'
import toastr from 'toastr'
import { findMatchBetweenPlayers } from '../../utils/match'
import PageLayout from '../common/layout/PageLayout'
import AvailabilityModal from './AvailabilityModal'
import MatchProposalModal from './MatchProposalModal'
import ProposalActionModal from './ProposalActionModal'
import { useWeeklyCalendarData } from '../../hooks/useWeeklyCalendarData'
import { useCalendarTimePreferences } from '../../hooks/useCalendarTimePreferences'
import WeeklyCalendarToolbar from './WeeklyCalendarToolbar'
import WeeklyCalendarCalendarView from './WeeklyCalendarCalendarView'
import OpponentListPopup from './OpponentListPopup'
import WeeklyCalendarListView from './WeeklyCalendarListView'
import ScheduledMatchModal from './ScheduledMatchModal'
import useIsMobile from './hooks/useIsMobile'
import useWeeklyCalendarTabs from './hooks/useWeeklyCalendarTabs'
import useWeeklyCalendarModals from './hooks/useWeeklyCalendarModals'
import usePendingProposals from './hooks/usePendingProposals'
import './WeeklyCalendar.css'

function WeeklyCalendar() {
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { activeTab, setActiveTab } = useWeeklyCalendarTabs()
  const {
    availabilityModal,
    proposalModal,
    opponentList,
    proposalActionModal,
    matchModal,
  } = useWeeklyCalendarModals()

  const { startHour, endHour, savePreferences, resetPreferences, isDefault } =
    useCalendarTimePreferences()

  const {
    loading,
    error,
    currentWeek,
    unplayedOpponents,
    opponentAvailability,
    weekDates,
    weekProposals,
    weekScheduledMatches,
    TIME_SLOTS,
    formatTimeSlot,
    isUserAvailable,
    getOpponentsAvailable,
    getUserAvailabilityForSlot,
    processOpponentSlots,
    hasUserOverlap,
    getMatchProposalForOpponent,
    getLatestProposalForOpponent,
    getScheduledMatchForOpponent,
    getExistingMatchWithOpponent,
    createOpponentClickData,
    navigateWeek,
    goToToday,
    handleMatchProposalConfirm,
    handleAvailabilitySave,
    handleAvailabilityDelete,
    handleProposalAction,
    userAvailability,
    divisionRoster,
    currentUserPlayerId,
    allMatches,
    divisionInfo,
    loadAvailabilityData,
  } = useWeeklyCalendarData({ startHour, endHour })

  const { pendingProposals, handleMatchProposalConfirmWrapper } =
    usePendingProposals(handleMatchProposalConfirm)

  const handleOpponentClick = (
    opponent,
    date,
    hour,
    minute = 0,
    availStart = null,
    availEnd = null,
  ) => {
    const clickData = createOpponentClickData(opponent, date, hour, minute)
    proposalModal.openProposalModal(
      clickData.opponent,
      clickData.proposedTime,
      availStart,
      availEnd,
    )
  }

  const handleDirectProposeMatch = (opponent) => {
    proposalModal.openProposalModal(opponent, null, null, null, true)
  }

  const handleOpponentSelection = (opponent) => {
    if (opponentList.selectedSlotTime) {
      const { date, timeSlot } = opponentList.selectedSlotTime
      handleOpponentClick(opponent, date, timeSlot.hour, timeSlot.minute)
      opponentList.closeOpponentList()
    }
  }

  const handleCellClick = (date, timeSlot, _hasOpponents) => {
    const userAvail = isUserAvailable(date, timeSlot)
    const availableOpponents = getOpponentsAvailable(date, timeSlot)

    if (availableOpponents.length > 0) {
      opponentList.openOpponentList(availableOpponents, { date, timeSlot })
    } else if (userAvail) {
      const availability = getUserAvailabilityForSlot(date, timeSlot)
      if (availability) {
        availabilityModal.openAvailabilityModal(
          'edit',
          date,
          timeSlot,
          availability,
        )
      }
    } else {
      availabilityModal.openAvailabilityModal('create', date, timeSlot, null)
    }
  }

  const handleProposalClick = (proposal, opponent) => {
    const matchWithOpponent = findMatchBetweenPlayers(
      allMatches,
      currentUserPlayerId,
      opponent.id,
    )
    proposalActionModal.openProposalActionModal(
      proposal,
      opponent,
      matchWithOpponent,
    )
  }

  const handleAvailabilitySaveWrapper = async (availabilityData) => {
    try {
      await handleAvailabilitySave(
        availabilityData,
        availabilityModal.modalMode,
      )
      toastr.success(
        availabilityData.apply_to === 'team'
          ? 'Team availability updated.'
          : 'Your availability saved.',
      )
    } catch (error) {
      console.error('Failed to save availability:', error)
      toastr.error(
        error.message || 'Unable to save availability. Please try again.',
      )
    }
  }

  const handleProposalActionWrapper = async (_action, _proposal) => {
    await handleProposalAction()
    proposalActionModal.closeProposalActionModal()
  }

  if (loading) {
    return (
      <PageLayout title="Weekly Calendar">
        <div className="loading-container">
          <p>Loading availability data...</p>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout title="Weekly Calendar">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error Loading Calendar</h4>
          <p>{error}</p>
          <hr />
          <button
            className="btn btn-primary"
            onClick={() => loadAvailabilityData()}
          >
            Retry
          </button>
        </div>
      </PageLayout>
    )
  }

  const days = isMobile
    ? ['S', 'M', 'T', 'W', 'T', 'F', 'S']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const isDoublesLeague = divisionInfo?.league_type === 'Doubles'
  const currentPlayer = (divisionInfo?.roster || []).find(
    (player) => Number(player.id) === Number(divisionInfo?.player_id),
  )
  const currentTeamId = currentPlayer?.partner?.team_id || null

  return (
    <PageLayout title="Weekly Calendar">
      <div className="weekly-calendar">
        <WeeklyCalendarToolbar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onAddAvailability={() =>
            availabilityModal.openAvailabilityModal(
              'create',
              new Date(),
              null,
              null,
            )
          }
        />

        {activeTab === 'calendar' && (
          <WeeklyCalendarCalendarView
            currentWeek={currentWeek}
            navigateWeek={navigateWeek}
            goToToday={goToToday}
            startHour={startHour}
            endHour={endHour}
            savePreferences={savePreferences}
            resetPreferences={resetPreferences}
            isDefault={isDefault}
            weekDates={weekDates}
            TIME_SLOTS={TIME_SLOTS}
            formatTimeSlot={formatTimeSlot}
            isUserAvailable={isUserAvailable}
            getOpponentsAvailable={getOpponentsAvailable}
            handleCellClick={handleCellClick}
            allMatches={allMatches}
            currentUserPlayerId={currentUserPlayerId}
            days={days}
            weekProposals={weekProposals}
            weekScheduledMatches={weekScheduledMatches}
            unplayedOpponents={unplayedOpponents}
            divisionRoster={divisionRoster}
            handleProposalClick={handleProposalClick}
            handleMatchClick={matchModal.openMatchModal}
          />
        )}

        <OpponentListPopup
          isOpen={opponentList.opponentListOpen}
          selectedSlotTime={opponentList.selectedSlotTime}
          selectedSlotOpponents={opponentList.selectedSlotOpponents}
          formatTimeSlot={formatTimeSlot}
          onClose={opponentList.closeOpponentList}
          onSelectOpponent={handleOpponentSelection}
          getMatchProposalForOpponent={getMatchProposalForOpponent}
          getScheduledMatchForOpponent={getScheduledMatchForOpponent}
          allMatches={allMatches}
          currentUserPlayerId={currentUserPlayerId}
        />

        {activeTab === 'list' && (
          <WeeklyCalendarListView
            unplayedOpponents={unplayedOpponents}
            opponentAvailability={opponentAvailability}
            processOpponentSlots={processOpponentSlots}
            handleOpponentClick={handleOpponentClick}
            handleDirectProposeMatch={handleDirectProposeMatch}
            hasUserOverlap={hasUserOverlap}
            getMatchProposalForOpponent={getMatchProposalForOpponent}
            getLatestProposalForOpponent={getLatestProposalForOpponent}
            getScheduledMatchForOpponent={getScheduledMatchForOpponent}
            getExistingMatchWithOpponent={getExistingMatchWithOpponent}
            pendingProposals={pendingProposals}
            currentUserPlayerId={currentUserPlayerId}
            allMatches={allMatches}
          />
        )}

        {userAvailability.length === 0 && (
          <div className="no-availability-message mt-3">
            <div className="alert alert-warning">
              <p className="mb-2">You haven't set your availability yet.</p>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() =>
                  availabilityModal.openAvailabilityModal(
                    'create',
                    new Date(),
                    null,
                    null,
                  )
                }
              >
                Add your availability
              </button>
              <span className="ms-2 text-muted">
                to see potential match times.
              </span>
            </div>
          </div>
        )}

        <AvailabilityModal
          isOpen={availabilityModal.modalOpen}
          mode={availabilityModal.modalMode}
          selectedDate={availabilityModal.selectedDate}
          selectedHour={availabilityModal.selectedHour}
          existingAvailability={availabilityModal.selectedAvailability}
          onClose={availabilityModal.closeAvailabilityModal}
          onSave={handleAvailabilitySaveWrapper}
          onDelete={handleAvailabilityDelete}
          leagueId={divisionInfo?.league_id}
          divisionId={divisionInfo?.division_id}
          teamId={currentTeamId}
          isDoublesLeague={isDoublesLeague}
          leagueEndDate={divisionInfo?.league_end_date}
        />

        <MatchProposalModal
          isOpen={proposalModal.proposalModalOpen}
          onClose={proposalModal.closeProposalModal}
          opponent={proposalModal.selectedOpponent}
          proposedTime={proposalModal.proposedTime}
          availabilityStart={proposalModal.availabilityStart}
          availabilityEnd={proposalModal.availabilityEnd}
          leagueId={leagueId}
          onConfirm={handleMatchProposalConfirmWrapper}
          directProposal={proposalModal.directProposal}
        />

        <ProposalActionModal
          isOpen={proposalActionModal.proposalActionModalOpen}
          onClose={proposalActionModal.closeProposalActionModal}
          proposal={proposalActionModal.selectedProposal}
          opponent={proposalActionModal.selectedProposalOpponent}
          onAction={handleProposalActionWrapper}
          currentPlayerId={currentUserPlayerId}
          match={proposalActionModal.selectedProposalMatch}
        />

        <ScheduledMatchModal
          isOpen={matchModal.matchModalOpen}
          selectedMatch={matchModal.selectedMatch}
          selectedMatchOpponent={matchModal.selectedMatchOpponent}
          closeMatchModal={matchModal.closeMatchModal}
          leagueId={leagueId}
          navigate={navigate}
        />
      </div>
    </PageLayout>
  )
}

export default WeeklyCalendar
